// 게임 이벤트 → 절차 효과음 결정(순수, c019). 파일 효과음 결정(../decide.ts)과 같은 이벤트를 읽지만 소리 표가 다르다(recipes.ts).
// 미리 할당한 SynthCueList에 쓰고, 큰 타격 판정(isHeavy)은 엔진이 덕킹을 거는 데 쓴다.
import { Ev, HitFlag, type GameEvent } from '../../shared/events';
import { GearCue, uniqueCueOf } from '../../shared/gearCues';
import type { HazardTheme } from '../../shared/levels';
import { BIG_ENHANCE, EnhanceOutcome, isTranscendEvent } from '../../ui/forgeFxModel';
import { BossKind } from '../../shared/types';
import { SYNTH_DEFS, Y } from './recipes';
import { S } from '../sounds';

export interface SynthCue {
  id: number;
  gainDb: number;
  /** 재생 속도(피치) 배율. 시드 미세 흔들림은 엔진이 더한다 */
  rate: number;
  x: number;
  z: number;
  positional: boolean;
}

export class SynthCueList {
  readonly items: SynthCue[];
  count = 0;
  constructor(capacity = 4) {
    this.items = [];
    for (let i = 0; i < capacity; i++) this.items.push({ id: 0, gainDb: 0, rate: 1, x: 0, z: 0, positional: false });
  }
  clear(): void { this.count = 0; }
  add(id: number, gainDb = 0, rate = 1, x = 0, z = 0): SynthCue | null {
    if (this.count >= this.items.length) return null;
    const c = this.items[this.count++];
    c.id = id; c.gainDb = gainDb; c.rate = rate; c.x = x; c.z = z;
    c.positional = SYNTH_DEFS[id].positional;
    return c;
  }
}

/** 줍기 등급(0 일반 1 희귀 2 영웅 3 세트 4 전설) → 소리 */
export const PICKUP_BY_GRADE: readonly number[] = [Y.pickupCommon, Y.pickupRare, Y.pickupHero, Y.pickupSet, Y.pickupLegend];
/** 큰 성공 단계(1 +10, 2 +13, 3 +15) → 재생 속도(반음씩 위로) */
export const BIG_TIER_RATE: readonly number[] = [1, 1, 1.0595, 1.1225];
/** 초월 단계(1..5 = +16..+20) → 재생 속도: +15 큰 성공(1.1225)에서 반음씩 더 위로 */
export const TRANSCEND_RATE: readonly number[] = [1, 1.1892, 1.2599, 1.3348, 1.4142, 1.4983];
/** gearCues 큐 종류 → 소리(-1 = 없음) */
export const UNIQUE_SOUND: readonly number[] = [-1, Y.uniqShield, Y.uniqHeal, Y.uniqMp, Y.uniqAwaken, Y.uniqRefund, Y.uniqLowHp];
/** Ev.BossPattern a(4 돌진 5 지진 6 세 갈래 물기 7 십자 8 포자 13 창날 세 줄 14 도끼 처형 15 소용돌이 16 별빛) → 경고 소리 */
export const TELE_BY_PATTERN: Readonly<Record<number, number>> = {
  4: Y.teleCharge, 5: Y.teleQuake, 6: Y.teleBite, 7: Y.teleCross, 8: Y.teleSpores,
  13: Y.teleLance, 14: Y.teleExecute, 15: Y.teleVortex, 16: Y.teleStar,
};

/** 이벤트 하나 → 절차 효과음 0~1개. theme = 지금 맵 위험 테마(view.map.hazardTheme) */
export function decideSynth(e: GameEvent, out: SynthCueList, theme: HazardTheme | undefined, hasSfxFile: (id: number) => boolean = () => true): void {
  switch (e.type) {
    case Ev.LootPickup: {
      // a 1 강화석, 2 보석(b = 색 번호, 등급이 아니다), 0 장비(b 등급). 펫이 주운 것(c 1)만 그 자리에서 들리고, 나머지는 화면 소리
      // 보석 전용 소리는 아직 없어 맑은 벨의 희귀 줍기음을 쓴다(색 번호를 등급으로 읽어 일반·영웅 소리가 나던 것을 막는다)
      const id = e.a === 1 ? Y.pickupStone : e.a === 2 ? Y.pickupRare : PICKUP_BY_GRADE[Math.max(0, Math.min(4, e.b | 0))];
      const c = out.add(id, 0, 1, e.x, e.z);
      if (c) c.positional = e.c === 1;
      break;
    }
    case Ev.LootDrop:
      // 세트·전설 장비 드롭 순간(lootFeed와 같은 문턱). 전설이 세트보다 조금 낮고 크게
      // 이름 있는 유니크(flags & 1, 등급은 영웅)도 전설처럼 울린다(PR #261 리뷰 P2: 연출만 있고 소리가 없었다)
      // 2026-11-03: 등급별 드롭은 파일 효과음(SeedAudio drop0~4, audio/decide.ts)이 맡는다. 그 드롭 파일 변형이 하나도 안 내려왔을 때만
      // 합성 dropRare로 대신한다(파일이 있으면 이중 재생이라 내지 않는다). hasSfxFile 기본값은 '있음'
      if (e.a === 0 && (e.b >= 3 || (e.flags & 1) !== 0)) {
        const big = e.b >= 4 || (e.flags & 1) !== 0;
        const fileId = S.drop0 + ((e.flags & 1) !== 0 ? 4 : Math.max(0, Math.min(4, e.b | 0)));
        if (!hasSfxFile(fileId)) out.add(Y.dropRare, big ? 0 : -2, big ? 0.94 : 1, e.x, e.z);
      }
      break;
    case Ev.GearEnhance: {
      const a = e.a | 0;
      if (isTranscendEvent(e.c | 0, e.b | 0)) {
        // 초월(C4): 성공 = 큰 성공 소리를 단계마다 높여 울리고 +18부터 맑은 성공음을 한 옥타브 위에 겹친다. 실패 = 유지(단계가 안 내려간다)
        if (a === EnhanceOutcome.Success) {
          const st = Math.max(1, Math.min(5, (e.b | 0) - 15));
          out.add(Y.forgeBig, 0, TRANSCEND_RATE[st]);
          if (st >= 3) out.add(Y.forgeSuccess, -4, 2);
        } else out.add(Y.forgeFail);
        break;
      }
      if (a === EnhanceOutcome.Success) {
        const tier = BIG_ENHANCE.indexOf((e.b | 0) as (typeof BIG_ENHANCE)[number]) + 1;
        if (tier > 0) out.add(Y.forgeBig, 0, BIG_TIER_RATE[tier]);
        else out.add(Y.forgeSuccess);
      } else if (a === EnhanceOutcome.Down) out.add(Y.forgeDown);
      else if (a === EnhanceOutcome.Protected) out.add(Y.forgeProtected);
      else out.add(Y.forgeFail);
      break;
    }
    case Ev.GearUnique: {
      const k = uniqueCueOf(e.a);
      const id = UNIQUE_SOUND[k] ?? -1;
      if (id >= 0 && k !== GearCue.None) out.add(id, 0, 1, e.x, e.z);
      break;
    }
    case Ev.HazardWarn:
      if (theme === 'frost') out.add(Y.iceSpikeWarn, 0, 1, e.x, e.z);
      else if (theme === 'venom') out.add(Y.gasWarn, 0, 1, e.x, e.z);
      else if (theme === 'sand') out.add(Y.sandWarn, 0, 1, e.x, e.z);
      else if (theme === 'void') out.add(Y.voidWarn, 0, 1, e.x, e.z);
      break;
    case Ev.HazardErupt:
      if (theme === 'frost') out.add(Y.iceSpike, 0, 1, e.x, e.z);
      else if (theme === 'venom') out.add(Y.gasBurst, 0, 1, e.x, e.z);
      else if (theme === 'sand') out.add(Y.sandBurst, 0, 1, e.x, e.z);
      else if (theme === 'void') out.add(Y.voidBurst, 0, 1, e.x, e.z);
      break;
    case Ev.HeroStatus:
      // a 1 서리 둔화 시작, 2 중독 시작, 3 상태 피해 틱(c 1 서리 동상, 2 중독), 4 모래 발 묶임 시작(sand), 5 침식 시작(void)
      // 틱(a 3)은 맵 테마로 소리를 가른다: sand의 c1 = 모래 긁힘, void의 c2 = 침식 틱, 그 밖의 테마는 기존 그대로
      if (e.a === 1) out.add(Y.frostCrack, -3, 1.12);
      else if (e.a === 2) out.add(Y.poisonPool);
      else if (e.a === 4) out.add(Y.sandSlow);
      else if (e.a === 5) out.add(Y.voidCorrode);
      else if (e.a === 3) out.add(e.c === 1 ? (theme === 'sand' ? Y.sandGrit : Y.frostCrack) : (theme === 'void' ? Y.voidTick : Y.poisonTick));
      break;
    case Ev.BossPattern: {
      // 잿불 비룡(레이드)의 패턴 번호는 다른 표(content/raid WyvernPattern)라 여기 경고음을 쓰지 않는다 — 레이드 절차음(raid/)이 맡는다
      if (e.b === BossKind.Wyvern) break;
      const id = TELE_BY_PATTERN[e.a | 0];
      // 소용돌이는 모래(r5)와 공허(r6) 보스가 같이 써서, 공허 맵에서는 같은 소리를 낮게 깔아 구분한다
      if (id !== undefined) out.add(id, 0, id === Y.teleVortex && theme === 'void' ? 0.88 : 1, e.x, e.z);
      // S5(game/bossRage RAGE_EVENT): 분노 10 = 높게 끓는 지진음, 광폭 11 = 낮고 크게, 광폭 두 번째 패턴 12 = 작은 십자 경고음(첫 패턴 경고음 위에 겹친다)
      else if (e.a === 10) out.add(Y.teleQuake, 0, 1.18, e.x, e.z);
      else if (e.a === 11) out.add(Y.teleQuake, 1, 0.84, e.x, e.z);
      else if (e.a === 12) out.add(Y.teleCross, -4, 1.12, e.x, e.z);
      break;
    }
    default:
      break;
  }
}

/** 큰 타격(덕킹 계기): 마무리·처형 타격, 보스 치명, 대량 처치, 각성 섬광, 연계 피니시, 가드 브레이크, 처형, 보스 처치 */
export function isHeavy(e: GameEvent): boolean {
  switch (e.type) {
    case Ev.Hit: {
      const f = e.flags | 0;
      if (e.b === 2) return false;
      return (f & (HitFlag.Finisher | HitFlag.Execute)) !== 0 || (e.b === 1 && (f & HitFlag.Crit) !== 0);
    }
    case Ev.MassKill: case Ev.LinkFinish: case Ev.GuardBreak: case Ev.Execute: case Ev.BossDefeated:
      return true;
    case Ev.AwakenPhase:
      return e.a === 2;
    default:
      return false;
  }
}
